import { useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import MoreMenu from '../../ui/MoreMenu';
import { StatusMark } from '../../ui/Pill';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { errorMessage } from '../../../lib/programs';
import { Deadline, dateText } from '../../../lib/eventMoney';
import type { SimplePanelProps } from '../adapt';
import { CountsOnly, useEventMoney, useMoneyRefresh } from './BudgetPanel';

// Same words as the Timeline: "Past its date", never "Late" or "Overdue".
const STATE: Record<Deadline['state'], string> = {
  done: 'Done',
  open: 'Coming',
  soon: 'This week',
  overdue: 'Past its date',
};

// Rows stack below 768px: header hidden, each cell on its own line after its column name.
const TABLE = 'w-full border-collapse tabular max-md:block';
const HEAD = 'text-left text-small text-ink-2 border-b border-line-strong max-md:hidden';
const TH = 'py-2 pr-3 last:pr-0 align-bottom font-semibold';
const ROW = 'border-b border-line hover:bg-selected max-md:block max-md:py-3';
const TD = 'py-[11px] pr-3 last:pr-0 align-top text-[15px] leading-[22px] text-ink max-md:block max-md:p-0';
const LABELLED =
  "max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-ink-3 max-md:before:text-small max-md:before:font-normal";

function MoveDialog({
  deadline,
  busy,
  error,
  onSave,
  onClose,
}: {
  deadline: Deadline;
  busy: boolean;
  error: unknown;
  onSave: (date: string) => void;
  onClose: () => void;
}) {
  const [date, setDate] = useState(deadline.dueOn ?? '');
  return (
    <Modal
      title="Change the date"
      why={`Moves “${deadline.label}”${deadline.vendor ? ` for ${deadline.vendor}` : ''} to the day you pick.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" disabled={!date || busy} onClick={() => onSave(date)}>
            Save the date
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <label className="block text-[15px] leading-5 font-semibold text-ink">
        Due on
        <input
          type="date"
          className="mt-[6px] block w-full max-w-[240px] min-h-[44px] px-3 py-[10px] border border-line-strong rounded-sm bg-surface text-[15px] text-ink font-normal"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </label>
      {error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(error, 'Could not save the deadline.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

/** A vendor's deadlines as a table: Done · Due · Deadline (· Vendor) · Status · "⋯" (Change the date). */
export function DeadlineList({
  eventId,
  canEdit,
  deadlines,
  showVendor = false,
}: {
  eventId: string;
  canEdit: boolean;
  deadlines: Deadline[];
  showVendor?: boolean;
}) {
  const refresh = useMoneyRefresh(eventId);
  const [moving, setMoving] = useState<Deadline | null>(null);
  const save = useMutation(
    async ({ d, body }: { d: Deadline; body: Record<string, unknown> }) =>
      api.patch(`/events/money/vendors/${d.vendorId}/deadlines/${d.key}`, body),
    {
      onSuccess: () => {
        setMoving(null);
        refresh();
      },
    },
  );
  return (
    <>
      <table className={TABLE}>
        <thead className={HEAD}>
          <tr>
            <th className={`${TH} w-[64px]`}>Done</th>
            <th className={`${TH} w-[120px] text-right`}>Due</th>
            <th className={TH}>Deadline</th>
            {showVendor ? <th className={TH}>Vendor</th> : null}
            <th className={TH}>Status</th>
            {canEdit ? (
              <th className={`${TH} w-[52px]`}>
                <span className="sr-only">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody className="max-md:block">
          {deadlines.map((d) => (
            <tr key={`${d.vendorId}-${d.key}`} className={ROW}>
              <td className={`${TD} max-md:float-left max-md:mr-3`}>
                <input
                  type="checkbox"
                  className="w-[18px] h-[18px] mt-[2px] accent-accent"
                  checked={Boolean(d.doneAt)}
                  disabled={!canEdit || save.isLoading}
                  onChange={() => save.mutate({ d, body: { done: !d.doneAt } })}
                  aria-label={`${d.label} done`}
                />
              </td>
              <td
                data-label="Due"
                className={`${TD} ${LABELLED} md:text-right whitespace-nowrap ${
                  d.state === 'overdue' ? 'font-semibold' : d.dueOn ? '' : 'text-ink-3'
                }`}
              >
                {d.dueOn ? dateText(d.dueOn) : '–'}
              </td>
              <td className={TD}>{d.label}</td>
              {showVendor ? (
                <td data-label="Vendor" className={`${TD} ${LABELLED} ${d.vendor ? '' : 'text-ink-3'}`}>
                  {d.vendor || '–'}
                </td>
              ) : null}
              <td data-label="Status" className={`${TD} ${LABELLED} whitespace-nowrap`}>
                {d.state === 'done' ? (
                  <span className="inline-flex items-center gap-[6px] text-ink-2">
                    <StatusMark mark="done" />
                    Done
                  </span>
                ) : (
                  <span className={d.state === 'overdue' ? 'font-semibold text-ink' : 'text-ink-2'}>
                    {STATE[d.state]}
                  </span>
                )}
              </td>
              {canEdit ? (
                <td className={`${TD} md:text-right max-md:pt-2`}>
                  {!d.doneAt ? (
                    <MoreMenu
                      label={`More for ${d.label}`}
                      items={[{ label: 'Change the date', onClick: () => setMoving(d) }]}
                    />
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {save.error && !moving ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the deadline.')}</ErrorNotice>
        </div>
      ) : null}
      {moving ? (
        <MoveDialog
          deadline={moving}
          busy={save.isLoading}
          error={save.error}
          onClose={() => {
            setMoving(null);
            save.reset();
          }}
          onSave={(date) => save.mutate({ d: moving, body: { dueOn: date } })}
        />
      ) : null}
    </>
  );
}

/** Vendor deadlines on the timeline: rooming list, room release, menu, kosher, meal guarantee. */
export default function VendorDeadlinesPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useEventMoney(eventId);
  if (q.isLoading) return <SkeletonRows rows={3} />;
  if (q.error || !q.data)
    return <ErrorNotice>{errorMessage(q.error, 'Could not load the vendor deadlines.')}</ErrorNotice>;
  const d = q.data;
  if (!d.visible)
    return (
      <Panel className="mt-14 max-md:mt-10">
        <PanelHeader title="Vendor deadlines" />
        <PanelBody>
          <CountsOnly data={d} />
        </PanelBody>
      </Panel>
    );
  const list = d.deadlines ?? [];
  return (
    <Panel className="mt-14 max-md:mt-10">
      <PanelHeader
        title="Vendor deadlines"
        note={
          list.length ? `${d.counts.deadlinesOverdue} past their date · ${d.counts.deadlinesSoon} this week` : undefined
        }
      />
      <PanelBody>
        {list.length ? (
          <DeadlineList eventId={eventId} canEdit={canEdit} deadlines={list} showVendor />
        ) : (
          <p className="text-[15px] leading-[22px] text-ink-2 max-w-[72ch]">
            None yet. Add the hotel and the venue on{' '}
            <span className="font-semibold text-ink">Budget &amp; vendors</span> and their deadlines appear here:
            rooming list and room release a month before, menu and kosher two weeks before, meal guarantee three
            business days before.
          </p>
        )}
      </PanelBody>
    </Panel>
  );
}
